import { useAuthStore } from '@/stores/authStore'
import { Tenants } from '@/models/tenants/Tenants.js'

/**
 * Route enum.
 *
 * !! If you add a route that requires a logged-in user, update the useAuthSync.REQUIRES_SESSION list !!
 * !! If you add a route that must only be seen logged out, update useAuthSync.REQUIRES_GUEST - or   !!
 * !! useAuthSync.GUEST_ONLY_ROUTES if it is NoEnforcement but calls a gateway 'guest' endpoint      !!
 */
class AuthGuard
{
	static NoEnforcement = 0
	static RequireLoggedOut = 1
	static RequireLoggedIn = 2
	static RequireAdmin = 3
	static RequireDataUpload = 4
	static RequireDocumentUpload = 5
	static RequireDocumentDownload = 6
	static RequirePermissions = 7
}

const noEnforcementGuard = async (to, from, next) =>
{
	next()
}

const requireLoggedOutGuard = async (to, from, next) =>
{
	const authStore = useAuthStore()
	if (!authStore.isLoggedIn)
	{
		next()
	}
	else
	{
		next('/dashboard')
	}
}

const requireLoggedInGuard = async (to, from, next) =>
{
	const authStore = useAuthStore()

	if (authStore.isLoggedIn)
	{
		//Check if route is enabled for tenant
		if (Tenants.isModuleEnabled(to.name))
		{
			next()
		}
		else
		{
			next('/dashboard')
		}
	}
	else
	{
		next('/auth/login')
	}
}

const requireAdminGuard = async (to, from, next) =>
{
	const authStore = useAuthStore()
	if (authStore.isLoggedIn && (authStore.isPortalAdmin || authStore.isOrganizationAdmin))
	{
		//Check if route is enabled for tenant
		if (Tenants.isModuleEnabled(to.name))
		{
			if (to.name === 'admin.support.messages' && !authStore.canManageSupport)
				next('/dashboard')
			else
				next()
		}
		else
		{
			next('/dashboard')
		}
	}
	else
	{
		next('/auth/login')
	}
}

/**
 * Prevent user from accessing upload page when they are not authorized to do so for their current organization
 * @param to
 * @param from
 * @param next
 * @returns {Promise<void>}
 */
const requireDataUploadGuard = async (to, from, next) =>
{
	const authStore = useAuthStore()
	if (authStore.isLoggedIn && (authStore.canUploadData || authStore.isPortalAdmin))
	{
		//Check if route is enabled for tenant
		if (Tenants.isModuleEnabled(to.name))
		{
			next()
		}
		else
		{
			next('/dashboard')
		}
	}
	else
	{
		next('/auth/login')
	}
}

/**
 * Prevent user from accessing document upload page when they are not authorized to do so for their current organization
 * @param to
 * @param from
 * @param next
 * @returns {Promise<void>}
 */
const requireDocumentUploadGuard = async (to, from, next) =>
{
	const authStore = useAuthStore()
	if (authStore.isLoggedIn && (authStore.canUploadDocuments || authStore.isPortalAdmin))
	{
		//Check if route is enabled for tenant
		if (Tenants.isModuleEnabled(to.name))
		{
			next()
		}
		else
		{
			next('/dashboard')
		}
	}
	else
	{
		next('/auth/login')
	}
}

/**
 * Prevent user from accessing document download page when they are not authorized to do so for their current organization
 * @param to
 * @param from
 * @param next
 * @returns {Promise<void>}
 */
const requireDocumentDownloadGuard = async (to, from, next) =>
{
	const authStore = useAuthStore()
	if (authStore.isLoggedIn && (authStore.canDownloadDocuments || authStore.isPortalAdmin))
	{
		//Check if route is enabled for tenant
		if (Tenants.isModuleEnabled(to.name))
		{
			next()
		}
		else
		{
			next('/dashboard')
		}
	}
	else
	{
		next('/auth/login')
	}
}

/**
 * Prevent user from accessing the destination page when they do not have the required permissions
 * The required permissions must be present in to.meta.permissions and must be present under getters in authStore.js
 * There are two types of permissions that can be set:
 * 	'ands' - must all be true
 * 	'ors' - must have at least one that is true
 * @param to
 * @param from
 * @param next
 * @returns {Promise<void>}
 */
const requirePermissionsGuard = async (to, from, next) =>
{
	const authStore = useAuthStore()

	let andsTrue = true
	let orsTrue = false

	if (Array.isArray(to.meta.permissions?.ands)) {
		to.meta.permissions.ands.forEach((permission) => {
			andsTrue = andsTrue && authStore[permission]
		})
	}

	if (Array.isArray(to.meta.permissions?.ors)) {
		to.meta.permissions.ors.forEach((permission) => {
			orsTrue = orsTrue || authStore[permission]
		})
	}

	if (andsTrue && orsTrue) {
		//Check if route is enabled for tenant
		if (Tenants.isModuleEnabled(to.name))
		{
			next()
		}
		else
		{
			next('/dashboard')
		}
	}
	else
	{
		console.log('Insufficient permissions.')
		next('/dashboard')
	}
}

export { AuthGuard, noEnforcementGuard, requireLoggedOutGuard, requireLoggedInGuard, requireAdminGuard, requireDataUploadGuard, requireDocumentUploadGuard, requireDocumentDownloadGuard, requirePermissionsGuard }
